/**
 * Property Investment Calculator — per-property analysis suite.
 * Four sub-tabs: Negative Gearing | ROI Projection | CGT | Scenarios
 * All calculations run client-side; no API calls needed.
 */
import React, { useState, useMemo } from 'react';
import {
  calcNegativeGearing, calcCGT, projectROI, buildScenarios,
  TAX_BRACKETS, AU_STATES,
} from '../../utils/propertyCalculations';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (n, d = 0) =>
  n == null || isNaN(n)
    ? '—'
    : (n < 0 ? '-' : '') +
      '$' +
      Math.abs(n).toLocaleString('en-AU', { minimumFractionDigits: d, maximumFractionDigits: d });

const fmtPct = (n, d = 1) =>
  n == null || isNaN(n) ? '—' : `${Number(n).toFixed(d)}%`;

const inp = {
  height: 36, padding: '0 10px', border: '1px solid #ddd', borderRadius: 6,
  fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box',
  background: '#fff',
};

const label = { fontSize: 11, fontWeight: 600, color: '#666', marginBottom: 4, display: 'block' };

function Field({ label: lbl, children }) {
  return (
    <div>
      <label style={label}>{lbl}</label>
      {children}
    </div>
  );
}

function ResultRow({ name, value, color, bold }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '7px 0', borderBottom: '1px solid #f3f3f3',
    }}>
      <span style={{ fontSize: 13, color: '#444', fontWeight: bold ? 700 : 400 }}>{name}</span>
      <span style={{ fontSize: 14, fontWeight: bold ? 800 : 600, color: color || '#222', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
}

function ResultBox({ children, title, color = '#1f3a2e' }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 10, padding: '16px 20px', flex: 1, minWidth: 220 }}>
      {title && <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color, marginBottom: 12 }}>{title}</div>}
      {children}
    </div>
  );
}

function GearingBadge({ gearing }) {
  const cfg = {
    positive: { bg: '#dcfce7', color: '#15803d', label: 'Positively Geared' },
    negative: { bg: '#fee2e2', color: '#dc2626', label: 'Negatively Geared' },
    neutral:  { bg: '#f3f4f6', color: '#6b7280', label: 'Neutral' },
  }[gearing] || { bg: '#f3f4f6', color: '#6b7280', label: gearing };
  return (
    <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: cfg.bg, color: cfg.color }}>
      {cfg.label}
    </span>
  );
}

// Simple SVG line chart
function LineChart({ rows, valueKey, color = '#1f3a2e', height = 140, label: chartLabel }) {
  if (!rows || rows.length < 2) return null;
  const vals = rows.map(r => r[valueKey]);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const w = 420, h = height, pad = { t: 10, b: 24, l: 8, r: 8 };
  const iw = w - pad.l - pad.r;
  const ih = h - pad.t - pad.b;
  const pts = rows.map((r, i) => [
    pad.l + (i / (rows.length - 1)) * iw,
    pad.t + ih - ((r[valueKey] - min) / range) * ih,
  ]);
  const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  return (
    <div>
      {chartLabel && <div style={{ fontSize: 11, color: '#999', fontWeight: 600, marginBottom: 4 }}>{chartLabel}</div>}
      <svg width="100%" viewBox={`0 0 ${w} ${h}`} style={{ display: 'block' }}>
        <path d={d} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" />
        {pts.map((p, i) => (
          <circle key={i} cx={p[0]} cy={p[1]} r="3" fill={color} />
        ))}
        {rows.filter((_, i) => i % Math.ceil(rows.length / 5) === 0 || i === rows.length - 1).map((r, _, arr) => {
          const origIdx = rows.indexOf(r);
          return (
            <text key={r.year} x={pts[origIdx][0]} y={h - 4} textAnchor="middle" fontSize="9" fill="#bbb">
              Yr {r.year}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

// ── Sub-tab: Negative Gearing ─────────────────────────────────────────────────

function NegativeGearingCalc({ inputs, onChange }) {
  const result = useMemo(() => calcNegativeGearing(inputs), [inputs]);

  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      {/* Inputs */}
      <div style={{ flex: '0 0 280px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#333', marginBottom: 4 }}>Inputs</div>

        <Field label="Weekly Rent ($)">
          <input style={inp} type="number" min={0} value={inputs.weeklyRent}
            onChange={e => onChange('weeklyRent', Number(e.target.value))} />
        </Field>
        <Field label="Loan Amount ($)">
          <input style={inp} type="number" min={0} value={inputs.loanAmount}
            onChange={e => onChange('loanAmount', Number(e.target.value))} />
        </Field>
        <Field label="Interest Rate (% p.a.)">
          <input style={inp} type="number" min={0} step={0.1} value={inputs.interestRate}
            onChange={e => onChange('interestRate', Number(e.target.value))} />
        </Field>
        <Field label="Marginal Tax Rate">
          <select style={inp} value={inputs.marginalTaxRate}
            onChange={e => onChange('marginalTaxRate', Number(e.target.value))}>
            {TAX_BRACKETS.map(b => (
              <option key={b.rate} value={b.rate}>{b.label}</option>
            ))}
          </select>
        </Field>
        <Field label="State / Territory">
          <select style={inp} value={inputs.state}
            onChange={e => onChange('state', e.target.value)}>
            {AU_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>

        <div style={{ fontWeight: 700, fontSize: 12, color: '#555', marginTop: 8 }}>Annual Expenses</div>
        {[
          ['rates', 'Council Rates ($)'],
          ['insurance', 'Insurance ($)'],
          ['propertyMgmt', 'Property Mgmt (%)'],
          ['repairs', 'Repairs & Maintenance ($)'],
          ['bodyCorp', 'Body Corp / Strata ($)'],
        ].map(([key, lbl]) => (
          <Field key={key} label={lbl}>
            <input style={inp} type="number" min={0} value={inputs.expenses[key]}
              onChange={e => onChange('expenses', { ...inputs.expenses, [key]: Number(e.target.value) })} />
          </Field>
        ))}
      </div>

      {/* Results */}
      <div style={{ flex: 1, minWidth: 260 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#333', marginBottom: 12 }}>Analysis</div>

        <div style={{ padding: '14px 18px', borderRadius: 10, background: '#f8f9fa', marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>Gearing Position</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <GearingBadge gearing={result.gearing} />
            <span style={{ fontSize: 13, color: '#666' }}>
              {result.gearing === 'negative'
                ? `${fmt(Math.abs(result.afterTaxWeeklyCost))}/wk out of pocket after tax`
                : result.gearing === 'positive'
                ? `${fmt(Math.abs(result.afterTaxWeeklyCost))}/wk positive after tax`
                : 'Break-even after tax'}
            </span>
          </div>
        </div>

        <ResultBox title="Income">
          <ResultRow name="Annual Rental Income" value={fmt(result.annualRent)} color="#15803d" />
        </ResultBox>

        <div style={{ height: 12 }} />
        <ResultBox title="Expenses">
          <ResultRow name="Loan Interest" value={fmt(result.interest)} />
          <ResultRow name="Council Rates" value={fmt(inputs.expenses.rates)} />
          <ResultRow name="Insurance" value={fmt(inputs.expenses.insurance)} />
          <ResultRow name="Property Management" value={fmt(result.mgmtFee)} />
          <ResultRow name="Repairs & Maintenance" value={fmt(inputs.expenses.repairs)} />
          <ResultRow name="Body Corporate" value={fmt(inputs.expenses.bodyCorp)} />
          <ResultRow name="Land Tax" value={fmt(result.landTax)} />
          <ResultRow name="Total Expenses" value={fmt(result.totalExpenses)} bold color="#dc2626" />
        </ResultBox>

        <div style={{ height: 12 }} />
        <ResultBox title="Net Position">
          <ResultRow name="Net Annual Position"
            value={fmt(result.netPosition)}
            color={result.gearing === 'positive' ? '#15803d' : '#dc2626'} bold />
          <ResultRow name="Tax Offset (deductible loss)"
            value={fmt(result.taxOffset)} color="#1a5baa" />
          <ResultRow name="Weekly After-Tax Cost / Gain"
            value={`${result.afterTaxWeeklyCost < 0 ? '-' : '+'}${fmt(Math.abs(result.afterTaxWeeklyCost))}`}
            color={result.afterTaxWeeklyCost >= 0 ? '#15803d' : '#dc2626'} bold />
        </ResultBox>
      </div>
    </div>
  );
}

// ── Sub-tab: ROI Projection ───────────────────────────────────────────────────

function ROICalc({ inputs, onChange }) {
  const result = useMemo(() => projectROI(inputs), [inputs]);

  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      {/* Inputs */}
      <div style={{ flex: '0 0 280px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#333', marginBottom: 4 }}>Projection Inputs</div>

        <Field label="Holding Period (years)">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input type="range" min={1} max={30} step={1} value={inputs.holdingYears}
              style={{ flex: 1 }}
              onChange={e => onChange('holdingYears', Number(e.target.value))} />
            <span style={{ minWidth: 28, fontSize: 14, fontWeight: 700, color: '#1f3a2e' }}>{inputs.holdingYears}yr</span>
          </div>
        </Field>
        <Field label="Annual Capital Growth (%)">
          <input style={inp} type="number" min={0} step={0.1} value={inputs.annualCapitalGrowth}
            onChange={e => onChange('annualCapitalGrowth', Number(e.target.value))} />
        </Field>
        <Field label="Annual Rental Growth (%)">
          <input style={inp} type="number" min={0} step={0.1} value={inputs.annualRentalGrowth}
            onChange={e => onChange('annualRentalGrowth', Number(e.target.value))} />
        </Field>
        <Field label="Purchase Price ($)">
          <input style={inp} type="number" min={0} value={inputs.purchasePrice}
            onChange={e => onChange('purchasePrice', Number(e.target.value))} />
        </Field>
        <Field label="Loan Amount ($)">
          <input style={inp} type="number" min={0} value={inputs.loanAmount}
            onChange={e => onChange('loanAmount', Number(e.target.value))} />
        </Field>
        <Field label="Interest Rate (% p.a.)">
          <input style={inp} type="number" min={0} step={0.1} value={inputs.interestRate}
            onChange={e => onChange('interestRate', Number(e.target.value))} />
        </Field>
        <Field label="Weekly Rent ($)">
          <input style={inp} type="number" min={0} value={inputs.weeklyRent}
            onChange={e => onChange('weeklyRent', Number(e.target.value))} />
        </Field>
        <Field label="Marginal Tax Rate">
          <select style={inp} value={inputs.marginalTaxRate}
            onChange={e => onChange('marginalTaxRate', Number(e.target.value))}>
            {TAX_BRACKETS.map(b => <option key={b.rate} value={b.rate}>{b.label}</option>)}
          </select>
        </Field>
      </div>

      {/* Results */}
      <div style={{ flex: 1, minWidth: 260 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#333', marginBottom: 12 }}>
          {inputs.holdingYears}-Year Projection
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          {[
            ['Total ROI', fmtPct(result.roi, 0), result.roi >= 0 ? '#15803d' : '#dc2626'],
            ['CAGR', fmtPct(result.cagr), result.cagr >= 0 ? '#1a5baa' : '#dc2626'],
            ['Cash-on-Cash (yr 1)', fmtPct(result.cashOnCash), result.cashOnCash >= 0 ? '#15803d' : '#dc2626'],
          ].map(([lbl, val, color]) => (
            <div key={lbl} style={{ flex: 1, minWidth: 120, background: '#f8f9fa', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: 11, color: '#888', fontWeight: 600, marginBottom: 4 }}>{lbl}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color }}>{val}</div>
            </div>
          ))}
        </div>

        <ResultBox title="Summary at Year " >
          <ResultRow name={`Projected Value (Yr ${inputs.holdingYears})`} value={fmt(result.finalValue)} color="#1f3a2e" bold />
          <ResultRow name="Capital Profit (after CGT)" value={fmt(result.capitalProfit)} color="#1a5baa" />
          <ResultRow name="Total After-Tax Cashflow" value={fmt(result.totalAfterTaxCashFlow)}
            color={result.totalAfterTaxCashFlow >= 0 ? '#15803d' : '#dc2626'} />
          <ResultRow name="Total Profit" value={fmt(result.totalProfit)}
            color={result.totalProfit >= 0 ? '#15803d' : '#dc2626'} bold />
        </ResultBox>

        {result.rows.length > 1 && (
          <div style={{ marginTop: 16, background: '#fff', border: '1px solid #e8e8e8', borderRadius: 10, padding: '16px 20px' }}>
            <LineChart rows={result.rows} valueKey="propertyValue" color="#1f3a2e" label="Property Value ($)" />
          </div>
        )}

        {result.rows.length > 0 && (
          <div style={{ marginTop: 16, background: '#fff', border: '1px solid #e8e8e8', borderRadius: 10, padding: '16px 20px' }}>
            <div style={{ fontSize: 11, color: '#999', fontWeight: 600, marginBottom: 8 }}>YEAR-BY-YEAR</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                    {['Year', 'Property Value', 'Annual Rent', 'Net Cashflow', 'After-Tax'].map(h => (
                      <th key={h} style={{ padding: '6px 10px', textAlign: 'right', fontSize: 10, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map(r => (
                    <tr key={r.year} style={{ borderBottom: '1px solid #f8f8f8' }}>
                      <td style={{ padding: '5px 10px', fontWeight: 700, color: '#555' }}>{r.year}</td>
                      <td style={{ padding: '5px 10px', textAlign: 'right' }}>{fmt(r.propertyValue)}</td>
                      <td style={{ padding: '5px 10px', textAlign: 'right', color: '#15803d' }}>{fmt(r.annualRent)}</td>
                      <td style={{ padding: '5px 10px', textAlign: 'right', color: r.netCashFlow >= 0 ? '#15803d' : '#dc2626' }}>{fmt(r.netCashFlow)}</td>
                      <td style={{ padding: '5px 10px', textAlign: 'right', fontWeight: 600, color: r.afterTaxCashFlow >= 0 ? '#15803d' : '#dc2626' }}>{fmt(r.afterTaxCashFlow)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sub-tab: CGT Calculator ───────────────────────────────────────────────────

function CGTCalc({ inputs, onChange }) {
  const [salePrice, setSalePrice] = useState(inputs.purchasePrice || 0);
  const [yearsHeld, setYearsHeld] = useState(inputs.holdingYears || 10);
  const result = useMemo(() => calcCGT(inputs, salePrice, yearsHeld), [inputs, salePrice, yearsHeld]);

  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      {/* Inputs */}
      <div style={{ flex: '0 0 280px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#333', marginBottom: 4 }}>Cost Base</div>

        <Field label="Purchase Price ($)">
          <input style={inp} type="number" min={0} value={inputs.purchasePrice}
            onChange={e => onChange('purchasePrice', Number(e.target.value))} />
        </Field>
        <Field label="Stamp Duty ($)">
          <input style={inp} type="number" min={0} value={inputs.stampDuty}
            onChange={e => onChange('stampDuty', Number(e.target.value))} />
        </Field>
        <Field label="Legal / Conveyancing Fees ($)">
          <input style={inp} type="number" min={0} value={inputs.legalFees}
            onChange={e => onChange('legalFees', Number(e.target.value))} />
        </Field>
        <Field label="Capital Improvements ($)">
          <input style={inp} type="number" min={0} value={inputs.improvements}
            onChange={e => onChange('improvements', Number(e.target.value))} />
        </Field>
        <Field label="Depreciation Claimed ($)">
          <input style={inp} type="number" min={0} value={inputs.depreciationClaimed}
            onChange={e => onChange('depreciationClaimed', Number(e.target.value))} />
        </Field>

        <div style={{ fontWeight: 700, fontSize: 13, color: '#333', marginTop: 8, marginBottom: 4 }}>Sale</div>

        <Field label="Sale Price ($)">
          <input style={inp} type="number" min={0} value={salePrice}
            onChange={e => setSalePrice(Number(e.target.value))} />
        </Field>
        <Field label="Selling Costs (% of sale price)">
          <input style={inp} type="number" min={0} step={0.1} value={inputs.sellingCostPct}
            onChange={e => onChange('sellingCostPct', Number(e.target.value))} />
        </Field>
        <Field label="Years Held">
          <input style={inp} type="number" min={0} step={1} value={yearsHeld}
            onChange={e => setYearsHeld(Number(e.target.value))} />
        </Field>
        <Field label="Marginal Tax Rate">
          <select style={inp} value={inputs.marginalTaxRate}
            onChange={e => onChange('marginalTaxRate', Number(e.target.value))}>
            {TAX_BRACKETS.map(b => <option key={b.rate} value={b.rate}>{b.label}</option>)}
          </select>
        </Field>
      </div>

      {/* Results */}
      <div style={{ flex: 1, minWidth: 260 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#333', marginBottom: 12 }}>CGT Analysis</div>

        <ResultBox title="Cost Base">
          <ResultRow name="Purchase Price" value={fmt(inputs.purchasePrice)} />
          <ResultRow name="Stamp Duty" value={fmt(inputs.stampDuty)} />
          <ResultRow name="Legal Fees" value={fmt(inputs.legalFees)} />
          <ResultRow name="Capital Improvements" value={fmt(inputs.improvements)} />
          <ResultRow name="Less: Depreciation Claimed" value={`-${fmt(inputs.depreciationClaimed)}`} />
          <ResultRow name="Total Cost Base" value={fmt(result.costBase)} bold />
        </ResultBox>

        <div style={{ height: 12 }} />
        <ResultBox title="Capital Gain">
          <ResultRow name="Sale Price" value={fmt(salePrice)} />
          <ResultRow name="Less Selling Costs" value={`-${fmt(result.sellingCosts)}`} />
          <ResultRow name="Gross Capital Gain" value={fmt(result.grossCapitalGain)}
            color={result.grossCapitalGain >= 0 ? '#1a5baa' : '#dc2626'} />
          {result.discountApplied && (
            <ResultRow name="50% CGT Discount Applied" value={`-${fmt(result.grossCapitalGain * 0.5)}`} color="#15803d" />
          )}
          <ResultRow name="Taxable Capital Gain" value={fmt(result.taxableGain)} bold
            color={result.taxableGain >= 0 ? '#1a5baa' : '#15803d'} />
        </ResultBox>

        <div style={{ height: 12 }} />
        <ResultBox title="Tax & Net Proceeds">
          <ResultRow name="CGT Liability" value={fmt(result.cgtLiability)} color="#dc2626" />
          <ResultRow name="Net Proceeds (after CGT)" value={fmt(result.netProceeds)} bold
            color={result.netProceeds >= (inputs.purchasePrice || 0) ? '#15803d' : '#dc2626'} />
          <ResultRow name="Profit vs Purchase Price" value={fmt(result.netProceeds - (inputs.purchasePrice || 0))}
            color={result.netProceeds >= (inputs.purchasePrice || 0) ? '#15803d' : '#dc2626'} />
        </ResultBox>

        {!result.discountApplied && yearsHeld < 1 && (
          <div style={{ marginTop: 12, padding: '10px 14px', background: '#fffbf0', borderRadius: 8, fontSize: 12, color: '#888', border: '1px solid #f0e8c0' }}>
            ⚠️ Held less than 12 months — 50% CGT discount does not apply.
          </div>
        )}
        <div style={{ marginTop: 12, padding: '10px 14px', background: '#f0f4ff', borderRadius: 8, fontSize: 12, color: '#555', border: '1px solid #c7d7f5' }}>
          ℹ️ Figures are estimates only. Consult a registered tax agent for advice on your specific situation.
        </div>
      </div>
    </div>
  );
}

// ── Sub-tab: Scenario Simulator ───────────────────────────────────────────────

function ScenarioCalc({ inputs, onChange }) {
  const scenarios = useMemo(() => buildScenarios(inputs), [inputs]);

  const scenarioList = [
    { key: 'bull', ...scenarios.bull },
    { key: 'base', ...scenarios.base },
    { key: 'bear', ...scenarios.bear },
  ];

  const rows = [
    { name: `Value at Yr ${inputs.holdingYears}`, key: 'finalValue', fmt: fmt },
    { name: 'Capital Profit (after CGT)', key: 'capitalProfit', fmt: fmt },
    { name: 'Total After-Tax Cashflow', key: 'totalAfterTaxCashFlow', fmt: fmt },
    { name: 'Total Profit', key: 'totalProfit', fmt: fmt, bold: true },
    { name: 'Total ROI', key: 'roi', fmt: (v) => fmtPct(v, 0) },
    { name: 'CAGR', key: 'cagr', fmt: fmtPct },
    { name: 'Cash-on-Cash (Yr 1)', key: 'cashOnCash', fmt: fmtPct },
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: '0 0 280px' }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#333', marginBottom: 10 }}>Base Assumptions</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Field label="Holding Period (years)">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="range" min={1} max={30} step={1} value={inputs.holdingYears}
                  style={{ flex: 1 }}
                  onChange={e => onChange('holdingYears', Number(e.target.value))} />
                <span style={{ minWidth: 28, fontSize: 14, fontWeight: 700, color: '#1f3a2e' }}>{inputs.holdingYears}yr</span>
              </div>
            </Field>
            <Field label="Base Capital Growth (% p.a.)">
              <input style={inp} type="number" min={0} step={0.5} value={inputs.annualCapitalGrowth}
                onChange={e => onChange('annualCapitalGrowth', Number(e.target.value))} />
            </Field>
            <Field label="Base Interest Rate (%)">
              <input style={inp} type="number" min={0} step={0.25} value={inputs.interestRate}
                onChange={e => onChange('interestRate', Number(e.target.value))} />
            </Field>
            <Field label="Marginal Tax Rate">
              <select style={inp} value={inputs.marginalTaxRate}
                onChange={e => onChange('marginalTaxRate', Number(e.target.value))}>
                {TAX_BRACKETS.map(b => <option key={b.rate} value={b.rate}>{b.label}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ marginTop: 14, padding: '10px 14px', background: '#f8f9fa', borderRadius: 8, fontSize: 12, color: '#777', border: '1px solid #eee' }}>
            <strong>Bull:</strong> +3% growth, -1% rate<br />
            <strong>Base:</strong> Your inputs as-is<br />
            <strong>Bear:</strong> -2% growth, +1.5% rate
          </div>
        </div>

        {/* Comparison table */}
        <div style={{ flex: 1, minWidth: 320, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase' }}>Metric</th>
                {scenarioList.map(s => (
                  <th key={s.key} style={{ padding: '8px 12px', textAlign: 'right', fontSize: 12, fontWeight: 800, color: s.color }}>
                    {s.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.key} style={{ borderBottom: '1px solid #f8f8f8' }}>
                  <td style={{ padding: '8px 12px', fontSize: 12, color: '#444', fontWeight: row.bold ? 700 : 400 }}>{row.name}</td>
                  {scenarioList.map(s => {
                    const v = s[row.key];
                    const isNeg = typeof v === 'number' && v < 0;
                    return (
                      <td key={s.key} style={{
                        padding: '8px 12px', textAlign: 'right', fontSize: 13, fontVariantNumeric: 'tabular-nums',
                        fontWeight: row.bold ? 800 : 600,
                        color: isNeg ? '#dc2626' : (row.bold ? s.color : '#333'),
                      }}>
                        {row.fmt(v)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Value trajectory chart for all 3 scenarios */}
      <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 10, padding: '16px 20px' }}>
        <div style={{ fontSize: 11, color: '#999', fontWeight: 600, marginBottom: 12 }}>PROPERTY VALUE TRAJECTORY</div>
        <div style={{ display: 'flex', gap: 16 }}>
          {scenarioList.map(s => (
            <div key={s.key} style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: s.color, marginBottom: 4 }}>{s.label}</div>
              <LineChart rows={s.rows} valueKey="propertyValue" color={s.color} height={100} />
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginTop: 12, padding: '10px 14px', background: '#fffbf0', borderRadius: 8, fontSize: 12, color: '#888', border: '1px solid #f0e8c0' }}>
        ⚠️ Scenario analysis is for illustrative purposes only. Past performance is not an indicator of future results. Not financial advice.
      </div>
    </div>
  );
}

// ── Main Export ───────────────────────────────────────────────────────────────

const CALC_TABS = [
  { key: 'gearing', label: 'Negative Gearing' },
  { key: 'roi', label: 'ROI Projection' },
  { key: 'cgt', label: 'CGT Calculator' },
  { key: 'scenarios', label: 'Scenario Simulator' },
];

export default function PropertyCalculatorTab({ property }) {
  const [calcTab, setCalcTab] = useState('gearing');

  // Shared mutable inputs — all four sub-calculators draw from the same state
  const [inputs, setInputs] = useState(() => ({
    purchasePrice: property.purchase_price || property.market_value || 0,
    loanAmount: property.mortgage_balance || 0,
    interestRate: property.mortgage_rate || 6.5,
    weeklyRent: property.rental_income_pw || 0,
    state: property.state || 'NSW',
    marginalTaxRate: 0.37,
    holdingYears: 10,
    annualCapitalGrowth: 3.5,
    annualRentalGrowth: 3.0,
    expenses: {
      rates: 1600,
      insurance: 1200,
      propertyMgmt: 8.5,
      repairs: 2000,
      bodyCorp: 0,
    },
    stampDuty: 0,
    legalFees: 2000,
    improvements: 0,
    depreciationClaimed: 0,
    sellingCostPct: 2.5,
  }));

  const onChange = (key, value) => {
    setInputs(prev => ({ ...prev, [key]: value }));
  };

  const tabProps = { inputs, onChange };

  return (
    <div style={{ paddingTop: 4 }}>
      <div style={{ marginBottom: 16, padding: '12px 16px', background: '#f8f9fa', borderRadius: 8, fontSize: 13, color: '#555', border: '1px solid #eee' }}>
        Pre-filled from property data. Adjust any input — changes sync across all calculators.
      </div>

      {/* Inner tab bar */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #e8e8e8', marginBottom: 24 }}>
        {CALC_TABS.map(t => (
          <button key={t.key} onClick={() => setCalcTab(t.key)} style={{
            padding: '8px 18px', fontSize: 13, fontWeight: calcTab === t.key ? 700 : 400,
            background: 'none', border: 'none', cursor: 'pointer',
            color: calcTab === t.key ? '#1f3a2e' : '#888',
            borderBottom: calcTab === t.key ? '2px solid #1f3a2e' : '2px solid transparent',
            marginBottom: -1,
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {calcTab === 'gearing' && <NegativeGearingCalc {...tabProps} />}
      {calcTab === 'roi' && <ROICalc {...tabProps} />}
      {calcTab === 'cgt' && <CGTCalc {...tabProps} />}
      {calcTab === 'scenarios' && <ScenarioCalc {...tabProps} />}
    </div>
  );
}
